import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useFeatureFlags } from './feature-flag-context';
import { useFeatureFlagShortcut } from './use-keyboard-shortcut';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { hotkeysDevtoolsPlugin } from '@tanstack/react-hotkeys-devtools';
type TProps = {
  showDevTools?: boolean;
};

export const FeatureFlagDialog: React.FC<TProps> = props => {
  const { showDevTools = false } = props;
  const { flags, toggleFeature, isDialogOpen, openDialog, closeDialog, allFeatureFlags } = useFeatureFlags();
  const theme = useTheme();
  const [hasChanges, setHasChanges] = useState(false);
  const initialFlagsRef = useRef<Record<string, boolean>>({});

  useFeatureFlagShortcut(openDialog);

  // Capture initial flag state when dialog opens
  useEffect(() => {
    if (isDialogOpen) {
      initialFlagsRef.current = { ...flags };
      setHasChanges(false);
    }
  }, [flags, isDialogOpen]);

  // Track changes
  useEffect(() => {
    if (!isDialogOpen) {
      return;
    }
    const changed = Object.keys(flags).some(key => flags[key] !== initialFlagsRef.current[key]);
    setHasChanges(changed);
  }, [flags, isDialogOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDialogOpen) {
        closeDialog();
      }
    };

    if (isDialogOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isDialogOpen, closeDialog]);

  const handleToggle = (flagKey: string) => {
    const flag = allFeatureFlags.find(f => f.key === flagKey);
    if (flag?.disabled) {
      return;
    }
    toggleFeature(flagKey);
  };

  const enabledCount = useMemo(() => allFeatureFlags.filter(f => flags[f.key]).length, [allFeatureFlags, flags]);

  return (
    <Fragment>
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.2)}`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
            px: 3,
            pt: 3,
            pb: 2.5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -30,
              right: -30,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: alpha('#fff', 0.08),
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -20,
              left: '30%',
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: alpha('#fff', 0.05),
            }}
          />

          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ position: 'relative' }}>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, letterSpacing: '-0.01em' }}>
                Feature Flags
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.75), mt: 0.5 }}>
                Toggle experimental features for development & testing
              </Typography>
            </Box>

            <IconButton
              aria-label="close"
              onClick={closeDialog}
              size="small"
              sx={{
                color: alpha('#fff', 0.7),
                backdropFilter: 'blur(8px)',
                background: alpha('#fff', 0.1),
                '&:hover': {
                  background: alpha('#fff', 0.2),
                  color: '#fff',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <IoClose size={18} />
            </IconButton>
          </Stack>

          {/* Stats bar */}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Chip
              label={`${enabledCount} active`}
              size="small"
              sx={{
                background: enabledCount > 0 ? alpha('#fff', 0.2) : alpha('#fff', 0.1),
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
                backdropFilter: 'blur(4px)',
              }}
            />
            <Chip
              label={`${allFeatureFlags.length} total`}
              size="small"
              sx={{
                background: alpha('#fff', 0.1),
                color: alpha('#fff', 0.8),
                fontWeight: 500,
                fontSize: '0.7rem',
                height: 24,
                backdropFilter: 'blur(4px)',
              }}
            />
            {hasChanges && (
              <Chip
                label="Unsaved changes"
                size="small"
                sx={{
                  background: alpha(theme.palette.warning.main, 0.3),
                  color: theme.palette.warning.light,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                  backdropFilter: 'blur(4px)',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.7 },
                  },
                }}
              />
            )}
          </Stack>
        </Box>

        {/* Content */}
        <DialogContent sx={{ p: 0 }}>
          {allFeatureFlags.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
              <Typography variant="h1" sx={{ fontSize: '3rem', mb: 1 }}>
                {'\u2690'}
              </Typography>
              <Typography variant="body1" color="text.secondary" fontWeight={500}>
                No feature flags available
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                Feature flags will appear here when configured
              </Typography>
            </Box>
          ) : (
            <Stack sx={{ py: 1 }}>
              {allFeatureFlags.map(flag => {
                const isEnabled = Boolean(flags[flag.key]);
                const wasChanged =
                  initialFlagsRef.current[flag.key] !== undefined && initialFlagsRef.current[flag.key] !== isEnabled;

                return (
                  <Box
                    key={flag.key}
                    onClick={() => handleToggle(flag.key)}
                    sx={{
                      px: 3,
                      py: 2,
                      cursor: flag.disabled ? 'not-allowed' : 'pointer',
                      opacity: flag.disabled ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04),
                      },
                      '&:active': {
                        background: alpha(theme.palette.primary.main, 0.08),
                      },
                      ...(wasChanged && {
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '20%',
                          bottom: '20%',
                          width: 3,
                          borderRadius: '0 2px 2px 0',
                          background: theme.palette.warning.main,
                        },
                      }),
                    }}
                  >
                    {/* Status indicator dot */}
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: isEnabled ? theme.palette.success.main : theme.palette.grey[300],
                        boxShadow: isEnabled ? `0 0 8px ${alpha(theme.palette.success.main, 0.5)}` : 'none',
                        transition: 'all 0.3s ease',
                      }}
                    />

                    {/* Label & description */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: isEnabled ? 'text.primary' : 'text.secondary',
                            transition: 'color 0.2s ease',
                          }}
                        >
                          {flag.label}
                        </Typography>
                        {flag.description?.includes('beta') && (
                          <Chip
                            label="BETA"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              letterSpacing: '0.05em',
                              background: alpha(theme.palette.info.main, 0.1),
                              color: theme.palette.info.main,
                              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            }}
                          />
                        )}
                      </Stack>
                      {flag.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.disabled',
                            fontSize: '0.875rem',
                            mt: 0.25,
                            lineHeight: 1.4,
                          }}
                        >
                          {flag.description}
                        </Typography>
                      )}
                    </Box>

                    {/* Toggle switch — iOS style */}
                    <Tooltip
                      title={
                        flag.disabled
                          ? (flag.disabledReason ?? 'This flag is disabled')
                          : isEnabled
                            ? 'Click to disable'
                            : 'Click to enable'
                      }
                      arrow
                      placement="left"
                    >
                      <span>
                        <Switch
                          checked={isEnabled}
                          disabled={flag.disabled}
                          onChange={() => handleToggle(flag.key)}
                          onClick={e => e.stopPropagation()}
                          focusVisibleClassName=".Mui-focusVisible"
                          disableRipple
                          sx={{
                            width: 48,
                            height: 28,
                            padding: 0,
                            '& .MuiSwitch-switchBase': {
                              padding: 0,
                              margin: '3px',
                              transitionDuration: '250ms',
                              '&.Mui-checked': {
                                transform: 'translateX(20px)',
                                color: '#fff',
                                '& + .MuiSwitch-track': {
                                  backgroundColor: theme.palette.success.main,
                                  opacity: 1,
                                  border: 0,
                                },
                                '& .MuiSwitch-thumb': {
                                  boxShadow: `0 0 6px ${alpha(theme.palette.success.main, 0.4)}`,
                                },
                              },
                              '&.Mui-focusVisible .MuiSwitch-thumb': {
                                border: `3px solid #fff`,
                              },
                            },
                            '& .MuiSwitch-thumb': {
                              boxSizing: 'border-box',
                              width: 22,
                              height: 22,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                              transition: 'box-shadow 0.2s ease',
                            },
                            '& .MuiSwitch-track': {
                              borderRadius: 28 / 2,
                              backgroundColor: theme.palette.grey[300],
                              opacity: 1,
                              transition: 'background-color 0.25s ease',
                            },
                          }}
                        />
                      </span>
                    </Tooltip>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: 'none',
            background: alpha(theme.palette.grey[50] ?? '#fafafa', 0.5),
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            Press
            <Box
              component="kbd"
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                fontWeight: 600,
                background: theme.palette.grey[200],
                border: `1px solid ${theme.palette.grey[300]}`,
                color: theme.palette.grey[600],
                boxShadow: `0 1px 0 ${theme.palette.grey[300]}`,
              }}
            >
              Alt+Shift+/
            </Box>
            to toggle
          </Typography>

          <Button
            onClick={closeDialog}
            variant="contained"
            size="medium"
            disableElevation
            sx={{
              px: 3,
              borderRadius: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.85rem',
              ...(hasChanges && {
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                },
              }),
            }}
          >
            {hasChanges ? 'Apply & Reload' : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>
      {showDevTools && <TanStackDevtools plugins={[hotkeysDevtoolsPlugin()]} />}
    </Fragment>
  );
};
